import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { Box, Flex, Text, VStack } from '@enechain/ecloud-designsystem'

import {
  POSITION_CHART_PLACEHOLDER_HEIGHT,
  PositionChart,
} from '~/components/bidding/common/PositionChart'
import type { PositionChartSlot } from '~/components/bidding/common/types'
import { convertAreaLabel } from '~/models/area'
import { ChartBidKindBadge } from '~/pages/SpotBidding/components/ChartBidKindBadge'

type Props = {
  /** 通常入札 + ブロック入札を合算した 48 コマ (`sharedPositionSlots`)。 */
  slots: PositionChartSlot[]
  /** need 取得エラー (need=0 で描画しつつ注記を出す)。 */
  needUnavailable?: boolean
  /** need が無い (区分がそのエリアに計画を持たない)。 [[PositionChart]] の同名 prop。 */
  needAbsent?: boolean
  /** いまどちらのタブで入札しているか (AC-BSP-016 の判別表示)。 */
  kind: 'normal' | 'block'
  /**
   * チャートの対象エリア = 入札一覧のエリアフィルタ (AC-BSP-019) で単一エリアを
   * 選択している時のそのエリア (AC-BSP-016a)。 undefined (複数エリア選択・0件選択の
   * いずれも) の間はチャート本体を描画せず、 代わりにガイドテキストを表示する。
   * 見出しのエリア名・「入札前 / 入札後」 の出し分けもこの 1 値から導く
   * (「単一エリアでない」 状態を空の `slots` で伝えると、 見出しが空配列を実データと
   * して読んでしまうため)。
   */
  area: AreaCode | undefined
  /** 札が 1 枚でもあるか (見出しの「入札前 / 入札後」の出し分け)。 */
  hasBids: boolean
  /**
   * コマクリック。 **未指定ならクリック無効**。
   *
   * ブロック入札タブ表示中は渡さない (2026-09-02 Des 判断 / #6596 Q2)。 実装に
   * prefill 付きブロック追加の経路が無く、 通常入札モーダルを開くと「ブロックを
   * 見ているのに通常入札が作られる」 ことになり、 判別表示を入れた趣旨と逆行する。
   */
  onSlotClick?: ((index: number) => void) | undefined
}

/**
 * 入札後ポジションチャートのパネル (タブバーの上に常設)。
 *
 * ## なぜタブの外に置くか
 *
 * design `bidding/spot.html:1021-1027` が 2026-07-10 にタブ内 mount + DOM 物理移動を
 * やめてタブ外へ出している。 理由もそこに書かれている:
 *
 * > 旧構成では通常入札タブ / ブロック入札タブそれぞれの中に mount して DOM 物理移動で
 * > 共有していたため、「通常入札とブロック入札の両方が 1 つのポジションに反映される」
 * > ことが構造から読み取れなかった。
 *
 * **「1 つのポジションである」 ことが構造で読める**のが採用理由 (2026-09-02 Des 判断 /
 * issue #6596 論点3)。 これは #6449 (タブ内バナーの種別が判別できない) とは逆向きに
 * 見えるが、 ポジションは**両タブの合算が 1 つ**である一方、 承認バナーは**タブごとに
 * 別物**なので扱いが違って正しい。
 *
 * ## タブ外に出したことで増える責務
 *
 * タブが判別の手掛かりにならなくなるので、 AC-BSP-016 の入札種別チップ
 * (`ChartBidKindBadge`) の重要度が上がる。 `kind` は表示中のタブから導出する。
 *
 * ## 統合前の姿
 *
 * 同型の JSX が `NormalBidTab` と `BlockBidTab` に 2 本あった。 差分は 見出しの
 * 出し分け・エリアラベルの有無・コマクリックの有無・器の背景色で、
 * 背景色だけブロック側が `Surface.primary` 固定と食い違っていた。 チャートが 1 本に
 * なる以上どちらか 1 つしか有り得ないので、 ブロック側の `Surface.primary` 固定に
 * 寄せた (下記「背景色を状態で塗り分けない理由」)。
 *
 * ## 背景色を状態で塗り分けない理由
 *
 * 48 コマ合算の充足率バッジと一緒に、 同じ合算値で駆動していた accordion の状態色
 * (旧 `coverageBadgeColorsOf(...).accordionBg` = 緑 / オレンジ) も撤去した。 design
 * `bidding/spot.html:1058-1064` / `bidding/intraday.html:318-322` (#7548 / AC-BIN-012)
 * が同じ判断を先に下している:
 *
 * > 同じ合算値で駆動していた accordion の状態色 (cov-state-*) も併せて撤去した
 * > (凡例となるバッジが無い状態で色だけ残すと理由が読み取れないため)。
 *
 * 凡例となる語が消えた後に色だけ残すと、 色だけに意味を持たせることになり
 * (WCAG 1.4.1)、 そもそも不足コマと余剰コマが打ち消し合う 48 コマ合算の 1 値を
 * 別の形で見せ続けることになる (2026-09-16 Des 判断 / issue #8125)。
 * コマ単位のカバー率はチャートのツールチップで維持する。
 *
 * ## 常時展開・スクロールで固定しない (AC-BSP-016)
 *
 * spec `spot-bidding.md` 機能詳細5 (issue #5157「常時展開表示に変更（折りたたみ
 * アコーディオンは廃止）」) に従い、 開閉可能な accordion ではなく常に展開した
 * パネルとして描く。 スクロール時に画面上部へ固定はしない (2026-10-08 デザイナー判断。
 * spec 機能詳細5 の「sticky挙動の詳細はDes判断範囲」。 旧: sticky top=0 で固定していた)。
 */
export const SpotPositionChartPanel = ({
  slots,
  needUnavailable = false,
  needAbsent = false,
  kind,
  area,
  hasBids,
  onSlotClick,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const isSingleAreaSelected = area !== undefined

  // 単一エリアでない間は「入札後のポジション」 固定でエリア名を出さない
  // (design `bidding/spot.html` renderSpotPositionChart の `_areaSel === null` 分岐。
  // 描いていないチャートについて札の有無を語らない)。
  const headingText =
    hasBids || !isSingleAreaSelected
      ? t('page:biddingCommon.chart.positionAfter')
      : t('page:biddingCommon.chart.positionBefore')

  return (
    // 器の枠・角丸は design `css/components.css:4019` の `.ecl-accordion`
    // (border 1px --border-low / radius --radius-lg = 8px) 準拠 (常時展開になっても
    // 見た目の器はそのまま維持する)。
    //
    // スクロールしても固定しない (冒頭 docstring 参照)。 zIndex はチャートの tooltip を
    // 後ろの sticky なタブ内フィルタ帯より前に出すため残し (旧 accordion 時代の値をそのまま
    // 継承)、 効かせるために position=relative を持たせる。 backgroundColor は状態に依らず
    // `Surface.primary` 固定 (上記「背景色を状態で塗り分けない理由」。 design `spot.html:1058`
    // `style="background:var(--surface-primary);"` と同趣旨)。 flexShrink={0} は
    // 呼び出し側の高さ拘束された column flex (`index.tsx` の
    // `<Flex flexDirection="column" ... height="100%">`) の既定 `flex-shrink:1` で
    // パネルが縦潰れするのを防ぐ (`SpotStatusHub` 配下の `Alert flexShrink={0}` と
    // 同じ理由。 レビュー指摘 2026-09-17, PR #8022)。
    <Box
      position="relative"
      zIndex={10}
      flexShrink={0}
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
      backgroundColor="Surface.primary"
    >
      {/* header の余白は design `bidding/spot.html:1059` の summary
          `padding: var(--space-3) var(--space-4)` = 上下 12px / 左右 16px。
          常時展開 (#5157) のため開閉トグルは持たない (= button でも
          AccordionItemTrigger でもない、 ただの見出し行)。 role="group" +
          aria-label は「入札前/後のポジション」 という 1 まとまりの見出しであることを
          支援技術にも伝えるため (`SupplyGap/components/GapChart.tsx` と同じ作法)。 */}
      <Flex
        alignItems="center"
        gap={3}
        aria-label={headingText}
        paddingX={4}
        paddingY={3}
        role="group"
      >
        <Text textStyle="sectionTitle" whiteSpace="nowrap">
          {headingText}
        </Text>
        {/* 入札種別の判別表示 (AC-BSP-016 [P0])。 経緯は部品側に集約。 */}
        <ChartBidKindBadge kind={kind} />
        {/* design `spot.html:6030` chart-area-label: 全選択=全エリア / 部分=エリア名連結 / 0件=未選択 */}
        {isSingleAreaSelected && (
          <Text textStyle="captionNormal" color="Text.midEmphasis">
            {convertAreaLabel(area)}
          </Text>
        )}
      </Flex>
      {/* overflow:visible は chart より高い tooltip が本文の枠で切れるのを防ぐ。
          余白は design `bidding/spot.html:1066` のチャート本文
          `padding: var(--space-3)` = 12px 四方、 上境界線は
          `css/components.css:4066` の `.ecl-accordion-content` 準拠。 */}
      <Box
        overflow="visible"
        padding={3}
        borderTopWidth="1px"
        borderTopColor="Border.lowEmphasis"
      >
        {isSingleAreaSelected ? (
          <PositionChart
            needAbsent={needAbsent}
            needUnavailable={needUnavailable}
            onSlotClick={onSlotClick}
            slots={slots}
          />
        ) : (
          /* AC-BSP-016a: 単一エリア以外 (複数エリア選択・0件選択のいずれも) では
             チャートを描画せず、 理由を示すガイドテキストに差し替える。
             高さ・背景は `IntradayBidding` の同型ガイド (AC-BIN-012b) と揃える。 */
          <VStack
            align="center"
            justify="center"
            gap={1}
            height={`${POSITION_CHART_PLACEHOLDER_HEIGHT}px`}
            textAlign="center"
            borderRadius="md"
            backgroundColor="Surface.secondary"
            paddingX={6}
          >
            <Text color="Text.midEmphasis">
              {t('page:biddingSpot.positionChart.areaGuide')}
            </Text>
          </VStack>
        )}
      </Box>
    </Box>
  )
}
